/**
 * ImageResizer-style helpers: append width/height to signed /api/assets URLs.
 * Signature does not cover size, so clients can pick a display size freely.
 */

const BUCKET = 64;
const MAX_DIMENSION = 4096;

function clampDim(n: number): number {
  return Math.max(1, Math.min(MAX_DIMENSION, Math.round(n)));
}

/** Round up to cache-friendly buckets so resize-drag does not thrash requests. */
export function bucketAssetDimension(cssPx: number, dpr = getDevicePixelRatio()): number {
  const target = cssPx * dpr;
  return clampDim(Math.max(BUCKET, Math.ceil(target / BUCKET) * BUCKET));
}

function getDevicePixelRatio(): number {
  if (typeof window === 'undefined') return 1;
  const dpr = window.devicePixelRatio || 1;
  return Number.isFinite(dpr) && dpr > 0 ? Math.min(dpr, 3) : 1;
}

function isOurAssetUrl(url: string): boolean {
  return url.includes('/api/assets/');
}

/**
 * Returns a URL with width/height query params for on-the-fly server resize.
 * Non-asset URLs (data:, external) are returned unchanged.
 */
export function withAssetResize(
  url: string,
  size: { width?: number; height?: number }
): string {
  if (!url || !isOurAssetUrl(url)) return url;

  const width = size.width != null && size.width > 0 ? clampDim(size.width) : undefined;
  const height = size.height != null && size.height > 0 ? clampDim(size.height) : undefined;
  if (width == null && height == null) return url;

  try {
    const abs = url.startsWith('http')
      ? new URL(url)
      : new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://local');

    if (width != null) abs.searchParams.set('width', String(width));
    else abs.searchParams.delete('width');

    if (height != null) abs.searchParams.set('height', String(height));
    else abs.searchParams.delete('height');

    if (url.startsWith('http')) return abs.toString();
    return `${abs.pathname}${abs.search}${abs.hash}`;
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    const parts: string[] = [];
    if (width != null) parts.push(`width=${width}`);
    if (height != null) parts.push(`height=${height}`);
    return parts.length ? `${url}${sep}${parts.join('&')}` : url;
  }
}

/**
 * Display-sized asset URL for a stage image (object box × DPR × zoom, bucketed).
 * Uses the longer edge so CSS stretch/cover still looks sharp.
 */
export function assetUrlForDisplaySize(
  url: string,
  displayWidth: number,
  displayHeight?: number,
  zoom = 1
): string {
  if (!url || !isOurAssetUrl(url)) return url;

  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  const wCss = Math.max(1, displayWidth) * z;
  const hCss = displayHeight != null && displayHeight > 0 ? displayHeight * z : wCss;
  const longEdge = Math.max(wCss, hCss);

  return withAssetResize(url, { width: bucketAssetDimension(longEdge) });
}
